import { from, Observable, Observer } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IDToken, AccessToken } from '@okta/okta-auth-js';
import { oktaConfig } from '@eda/shared/okta.config';
import { CacheFactoryService } from '@eda/shared/cache/cache-factory';
import { ConversationIdGenerator } from '../../shared/conversation-id-generator';
import { OKTA_AUTH } from '@okta/okta-angular';
import { environment } from 'src/environments/environment';

@Injectable({providedIn: 'root'})
export class OktaAuthService {

  private cacheFactoryService = inject(CacheFactoryService);
  private conversationIdGenerator = inject(ConversationIdGenerator);
  private readonly oktaAuth = inject(OKTA_AUTH);
  

  $isAuthenticated: Observable<boolean>;
  private observer?: Observer<boolean>;
  constructor(private router: Router) {
    this.$isAuthenticated = new Observable((observer: Observer<boolean>) => {
      this.observer = observer;
      this.isAuthenticated().then(val => {
        observer.next(val);
      });
    });
  }

  async isAuthenticated(): Promise<boolean> {
    const token = await this.oktaAuth.tokenManager.get('accessToken');
    if (!token) {
      return false;
    }
    if (this.oktaAuth.tokenManager.hasExpired(token)) { 
      return false;
    }
    return true;
  }

  login(originalUrl: string) {
    // Save current URL before redirect
    sessionStorage.setItem('okta-app-url', originalUrl || this.router.url);

    // Launches the login redirect.
    this.oktaAuth.token.getWithRedirect({
      scopes: environment.oktaConfig.scopes
    });
  }

  async handleAuthentication() {
    const tokenContainer = await this.oktaAuth.token.parseFromUrl();
    const idToken = tokenContainer.tokens.idToken as IDToken;
    const accessToken = tokenContainer.tokens.accessToken as AccessToken;
    
    this.oktaAuth.tokenManager.add('idToken', idToken);
    this.oktaAuth.tokenManager.add('accessToken', accessToken);

    if (await this.isAuthenticated()) {
      this.observer?.next(true);
    }

    // Retrieve the saved URL and navigate back
    const url = sessionStorage.getItem('okta-app-url') as string;
    this.router.navigateByUrl(url);
  }

  getAccessToken(): Observable<AccessToken | undefined> {
    return from(this.oktaAuth.tokenManager.get('accessToken') as Promise<AccessToken | undefined>);
  }

  async logout() {
    // clear all session, and than logout // to prevent any potential loop back
    this.oktaAuth.clearStorage();
    this.oktaAuth.closeSession();
    this.oktaAuth.clearDPoPStorage(true);
    this.cacheFactoryService.flushAllCache();
    this.conversationIdGenerator.removeConversationId();
    // end of clear all session
    await this.oktaAuth.signOut({
      postLogoutRedirectUri: oktaConfig.logoutUrl
    });
  }
}
